<template>
  <div
    class="relative w-9 sm:w-56"
    ref="dropdownRoot"
    @keydown.escape="closeDropdown"
  >
    <button
      type="button"
      ref="triggerRef"
      class="flex items-center justify-center sm:justify-start gap-2 w-full h-9 sm:px-2.5 rounded-md border border-container-border dark:border-container-border-dark bg-input-bg dark:bg-input-bg-dark hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40 dark:focus-visible:ring-accent-dark/40"
      @click="isOpen = !isOpen"
      aria-haspopup="listbox"
      :aria-expanded="isOpen"
      :aria-label="t('components.membershipSelector.switchOrganization')"
      :title="t('components.membershipSelector.switchOrganization')"
    >
      <MdiOfficeBuilding
        class="text-brand-orange dark:text-accent-dark text-[18px] shrink-0"
        aria-hidden="true"
      />

      <span
        class="hidden sm:block flex-1 text-left text-sm font-medium text-heading dark:text-heading-dark truncate"
      >
        {{ selectedMembership.organizationName }}
      </span>

      <MdiChevronDown
        class="hidden sm:block text-muted-text dark:text-muted-text-dark text-[16px] shrink-0"
        aria-hidden="true"
      />
    </button>

    <transition
      enter-active-class="transition ease-out duration-100"
      enter-from-class="transform opacity-0 scale-95"
      enter-to-class="transform opacity-100 scale-100"
      leave-active-class="transition ease-in duration-75"
      leave-from-class="transform opacity-100 scale-100"
      leave-to-class="transform opacity-0 scale-95"
    >
      <div
        v-if="isOpen"
        class="absolute top-full right-0 mt-1.5 w-64 max-w-[calc(100vw-2rem)] bg-box-container-bg dark:bg-box-container-bg-dark border border-container-border dark:border-container-border-dark rounded-lg shadow-dropdown z-40 p-1.5"
      >
        <p
          class="px-2.5 pt-1.5 pb-1 text-[11px] uppercase tracking-wider font-semibold text-muted-text dark:text-muted-text-dark"
        >
          {{ t("components.membershipSelector.switchOrganization") }}
        </p>
        <ul class="space-y-0.5" role="listbox">
          <li
            v-for="membership in memberships"
            :key="membership.organizationId"
          >
            <button
              type="button"
              role="option"
              :aria-selected="
                membership.organizationId === selectedMembership.organizationId
              "
              @click="selectMembershipAndClose(membership.organizationId)"
              class="w-full flex items-center justify-between gap-2 h-9 px-2.5 rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              :class="
                membership.organizationId ===
                selectedMembership.organizationId
                  ? 'bg-brand-orange/10 dark:bg-accent-soft-dark hover:bg-brand-orange/15 dark:hover:bg-accent-soft-hover-dark'
                  : 'hover:bg-secondary-bg dark:hover:bg-secondary-bg-dark'
              "
            >
              <span
                class="text-sm truncate"
                :class="
                  membership.organizationId ===
                  selectedMembership.organizationId
                    ? 'text-brand-orange dark:text-accent-dark font-medium'
                    : 'text-content dark:text-content-dark'
                "
              >
                {{ membership.organizationName }}
              </span>

              <!-- Filled badge, not a bare check: reads as "this is the
                   active organization" together with the tinted row. -->
              <MdiCheckCircle
                v-if="
                  membership.organizationId ===
                  selectedMembership.organizationId
                "
                class="text-brand-orange dark:text-accent-dark text-[16px] shrink-0"
                aria-hidden="true"
              />
            </button>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { User } from "tf-api-client";
import { ref, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ useScope: "global" });

const props = defineProps<{
  memberships: User.Membership[];
  selectedMembership: User.Membership;
  onSelect: (membershipId: string) => void;
}>();

const isOpen = ref(false);
const dropdownRoot = ref<HTMLDivElement | null>(null);
const triggerRef = ref<HTMLButtonElement | null>(null);

// Escape closes the dropdown and hands focus back to its trigger.
const closeDropdown = () => {
  if (!isOpen.value) return;
  isOpen.value = false;
  triggerRef.value?.focus();
};

const selectMembershipAndClose = (membershipId: string) => {
  props.onSelect(membershipId);
  isOpen.value = false;
  triggerRef.value?.focus();
};

const handleClickOutside = (event: MouseEvent) => {
  if (
    dropdownRoot.value &&
    !dropdownRoot.value.contains(event.target as Node)
  ) {
    isOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>
